// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';

// material ui
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';

// feature-components
import BuildingInfoSubheader from '@/features/energyxplorer/portfolio/components/meter-drawer/BuildingInfoSubheader';
// utils
import { formatNumber } from '@/utils/formatters';
import type {
  ElectricityComponentKey,
  ElectricityFactorDetails,
  HeatComponentKey,
  HeatFactorDetails,
  PortfolioReMoniFactor,
  ResourceName,
  ScoreComponentDetails,
  WaterComponentKey,
  WaterFactorDetails,
} from '@/utils/remoniScoreCalculator';

interface PortfolioHighlightDetailsContentProps {
  metric: PortfolioReMoniFactor;
  onOpenAnalysis?: (metric: PortfolioReMoniFactor) => void;
  attentionLimit?: number;
}

interface AttentionItem {
  id: string;
  resource: ResourceName;
  label: string;
  rawValue: string;
  score: string;
  scoreNumeric: number;
  contribution: string;
  interpretation: string | null;
  suggestions: string[];
}

interface MetricDisplayItem {
  key: string;
  label: string;
  value: string;
}

interface MetricSection {
  key: ResourceName;
  title: string;
  factor: string;
  items: MetricDisplayItem[];
}

const COMPONENT_SCORE_NAMESPACE = 'component-score-descriptions';

type Translate = (key: string, options?: Record<string, unknown>) => string;

function getScoreLevel(score: number): 'high' | 'medium' | 'low' {
  if (score >= 80) return 'high';
  if (score >= 50) return 'medium';
  return 'low';
}

function getScoreInterpretation(
  t: Translate,
  resource: ResourceName,
  component: string,
  score: number,
): string | null {
  const level = getScoreLevel(score);
  const interpretation = t(
    `component_scores.${resource}.${component}.score_interpretation.${level}`,
    {
      ns: COMPONENT_SCORE_NAMESPACE,
      defaultValue: '',
    },
  );
  return interpretation || null;
}

function formatMetricValue(
  value: number | null | undefined,
  suffix?: string,
): string {
  const formatted = formatNumber(value);
  if (formatted === '-' || !suffix) {
    return formatted;
  }

  return `${formatted} ${suffix}`;
}

function getResourceLabel(resource: ResourceName, t: Translate) {
  return t(`portfolio.highlights.resources.${resource}`);
}

function getComponentLabel(
  resource: ResourceName,
  component: ElectricityComponentKey | HeatComponentKey | WaterComponentKey,
  t: Translate,
) {
  return t(`portfolio.highlights.component_labels.${resource}.${component}`);
}

function getComponentSuggestions(
  t: Translate,
  resource: ResourceName,
  componentKey: string,
): string[] {
  const suggestions = t(
    `component_scores.${resource}.${componentKey}.improvement_suggestions`,
    {
      ns: COMPONENT_SCORE_NAMESPACE,
      returnObjects: true,
      defaultValue: [],
    },
  ) as unknown;

  return Array.isArray(suggestions) ? suggestions : [];
}

function buildAttentionItems(
  metric: PortfolioReMoniFactor,
  t: Translate,
): AttentionItem[] {
  const items: Array<AttentionItem & { contributionValue: number }> = [];

  const addComponentItems = <Key extends string>(
    resource: ResourceName,
    components: Record<Key, ScoreComponentDetails> | undefined,
  ) => {
    if (!components) {
      return;
    }

    (
      Object.entries(components) as Array<[string, ScoreComponentDetails]>
    ).forEach(([componentKey, comp]) => {
      if (!comp.available || comp.contribution <= 0) {
        return;
      }

      const interpretation = getScoreInterpretation(
        t,
        resource,
        componentKey,
        comp.score,
      );
      const suggestions = getComponentSuggestions(t, resource, componentKey);

      items.push({
        id: `${resource}-${componentKey}`,
        resource,
        label: getComponentLabel(
          resource,
          componentKey as
            ElectricityComponentKey | HeatComponentKey | WaterComponentKey,
          t,
        ),
        rawValue: formatNumber(comp.rawValue),
        score: formatNumber(comp.score),
        scoreNumeric: comp.score,
        contribution: formatNumber(comp.contribution),
        contributionValue: comp.contribution,
        interpretation,
        suggestions,
      });
    });
  };

  addComponentItems('electricity', metric.electricity?.components);
  addComponentItems('heat', metric.heat?.components);
  addComponentItems('water', metric.water?.components);

  return (
    items
      .sort((left, right) => right.contributionValue - left.contributionValue)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .map(({ contributionValue, ...item }) => item)
  );
}

function buildElectricitySection(
  electricity: ElectricityFactorDetails,
  t: Translate,
): MetricSection {
  return {
    key: 'electricity',
    title: getResourceLabel('electricity', t),
    factor: formatNumber(electricity.factor),
    items: [
      {
        key: 'outsideKW',
        label: getComponentLabel('electricity', 'outsideKW', t),
        value: formatMetricValue(electricity.outsideKW, 'kW'),
      },
      {
        key: 'outsideRatio',
        label: getComponentLabel('electricity', 'outsideRatio', t),
        value: formatMetricValue(
          electricity.outsideRatio !== null
            ? electricity.outsideRatio * 100
            : null,
          '%',
        ),
      },
      {
        key: 'trend',
        label: getComponentLabel('electricity', 'trend', t),
        value: formatMetricValue(electricity.trendImpactKW, 'kW'),
      },
      {
        key: 'averageKW',
        label: t('portfolio.highlights.metric_labels.electricity.average_kw'),
        value: formatMetricValue(electricity.averageKW, 'kW'),
      },
      {
        key: 'lastYearConsumption',
        label: t(
          'portfolio.highlights.metric_labels.shared.last_year_consumption',
        ),
        value: formatMetricValue(electricity.lastYearConsumption, 'kWh'),
      },
    ],
  };
}

function buildHeatSection(
  heat: HeatFactorDetails,
  t: Translate,
): MetricSection {
  return {
    key: 'heat',
    title: getResourceLabel('heat', t),
    factor: formatNumber(heat.factor),
    items: [
      {
        key: 'heatingSeason',
        label: getComponentLabel('heat', 'heatingSeason', t),
        value: formatMetricValue(heat.heatingSeasonKW, 'kW'),
      },
      {
        key: 'outsideHeatingSeason',
        label: getComponentLabel('heat', 'outsideHeatingSeason', t),
        value: formatMetricValue(heat.outsideHeatingSeasonKW, 'kW'),
      },
      {
        key: 'last30',
        label: getComponentLabel('heat', 'last30', t),
        value: formatMetricValue(heat.last30KW, 'kW'),
      },
      {
        key: 'trend12',
        label: getComponentLabel('heat', 'trend12', t),
        value: formatMetricValue(heat.trend12ImpactKW, 'kW'),
      },
      {
        key: 'trend30',
        label: getComponentLabel('heat', 'trend30', t),
        value: formatMetricValue(heat.trend30ImpactKW, 'kW'),
      },
    ],
  };
}

function buildWaterSection(
  water: WaterFactorDetails,
  t: Translate,
): MetricSection {
  return {
    key: 'water',
    title: getResourceLabel('water', t),
    factor: formatNumber(water.factor),
    items: [
      {
        key: 'outside30',
        label: getComponentLabel('water', 'outside30', t),
        value: formatMetricValue(water.outside30M3h, 'm³/h'),
      },
      {
        key: 'trend12',
        label: getComponentLabel('water', 'trend12', t),
        value: formatMetricValue(water.trend12ImpactM3h, 'm³/h'),
      },
      {
        key: 'trend30',
        label: getComponentLabel('water', 'trend30', t),
        value: formatMetricValue(water.trend30ImpactM3h, 'm³/h'),
      },
      {
        key: 'lastYearConsumption',
        label: t(
          'portfolio.highlights.metric_labels.shared.last_year_consumption',
        ),
        value: formatMetricValue(water.lastYearConsumption, 'm³'),
      },
    ],
  };
}

function buildMetricSections(
  metric: PortfolioReMoniFactor,
  t: Translate,
): MetricSection[] {
  const sections: MetricSection[] = [];

  if (metric.electricity) {
    sections.push(buildElectricitySection(metric.electricity, t));
  }

  if (metric.heat) {
    sections.push(buildHeatSection(metric.heat, t));
  }

  if (metric.water) {
    sections.push(buildWaterSection(metric.water, t));
  }

  return sections;
}

const PortfolioHighlightDetailsContent = ({
  metric,
  onOpenAnalysis,
  attentionLimit,
}: PortfolioHighlightDetailsContentProps) => {
  const { t } = useTranslation(['energy-analysis', COMPONENT_SCORE_NAMESPACE]);
  const attentionItems = buildAttentionItems(metric, t).slice(
    0,
    attentionLimit ?? Number.POSITIVE_INFINITY,
  );
  const metricSections = buildMetricSections(metric, t);

  return (
    <Stack gap={3}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        gap={2}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {metric.address}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {metric.matrikelKey}
          </Typography>
        </Box>
        {onOpenAnalysis ? (
          <Button
            variant="contained"
            size="small"
            startIcon={<OpenInNewRoundedIcon />}
            onClick={() => onOpenAnalysis(metric)}
          >
            {t('portfolio.highlights.open_analysis')}
          </Button>
        ) : null}
      </Stack>

      <BuildingInfoSubheader matrikelKey={metric.matrikelKey} />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="overline" color="text.secondary">
              {t('portfolio.highlights.summary.remoni_factor')}
            </Typography>
            <Typography variant="h4" fontWeight={700}>
              {formatNumber(metric.remoniFactor)}
            </Typography>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="overline" color="text.secondary">
              {t('portfolio.highlights.summary.highest_resource')}
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {metric.highestResource
                ? getResourceLabel(metric.highestResource, t)
                : t('portfolio.highlights.no_data')}
            </Typography>
            {metric.highestFactor !== null ? (
              <Typography variant="body2" color="text.secondary">
                {t('portfolio.highlights.summary.factor_value', {
                  value: formatNumber(metric.highestFactor),
                })}
              </Typography>
            ) : null}
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="overline" color="text.secondary">
              {t('portfolio.highlights.summary.average_factor')}
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {formatNumber(metric.averageFactor)}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      <Box>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {t('portfolio.highlights.sections.attention')}
        </Typography>
        {attentionItems.length ? (
          <Grid container spacing={2}>
            {attentionItems.map((item) => (
              <Grid key={item.id} size={{ xs: 12, md: 6 }}>
                <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <Stack gap={1.5}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}
                    >
                      <Typography variant="subtitle2" fontWeight={700}>
                        {item.label}
                      </Typography>
                      <Chip
                        size="small"
                        label={getResourceLabel(item.resource, t)}
                      />
                    </Stack>

                    <Stack direction="row" gap={2} flexWrap="wrap">
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('portfolio.highlights.labels.raw_value')}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {item.rawValue}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('portfolio.highlights.labels.score')}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {item.score}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('portfolio.highlights.labels.contribution')}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {item.contribution}
                        </Typography>
                      </Box>
                    </Stack>

                    {item.interpretation ? (
                      <Box
                        sx={{
                          pt: 1,
                          borderTop: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {t('component_scores.interpretation_label')}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {item.interpretation}
                        </Typography>
                      </Box>
                    ) : null}

                    {item.suggestions.length > 0 ? (
                      <Box sx={{ pt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('component_scores.improvements_label')}
                        </Typography>
                        <Typography
                          variant="body2"
                          component="div"
                          sx={{ mt: 0.5 }}
                        >
                          <ul
                            style={{
                              margin: '0.25rem 0',
                              paddingLeft: '1.25rem',
                            }}
                          >
                            {item.suggestions.map((suggestion, idx) => (
                              <li key={idx}>
                                <Typography variant="body2" component="span">
                                  {suggestion}
                                </Typography>
                              </li>
                            ))}
                          </ul>
                        </Typography>
                      </Box>
                    ) : null}
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography color="text.secondary">
            {t('portfolio.highlights.no_data')}
          </Typography>
        )}
      </Box>

      <Divider />

      <Box>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {t('portfolio.highlights.sections.main_metrics')}
        </Typography>
        <Grid container spacing={2}>
          {metricSections.map((section) => (
            <Grid key={section.key} size={{ xs: 12, md: 4 }}>
              <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Stack gap={1.5}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {section.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('portfolio.highlights.summary.factor_value', {
                        value: section.factor,
                      })}
                    </Typography>
                  </Box>

                  <Stack gap={1}>
                    {section.items.map((item) => (
                      <Box key={item.key}>
                        <Typography variant="caption" color="text.secondary">
                          {item.label}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Stack>
  );
};

export default PortfolioHighlightDetailsContent;
